import { Injectable, signal, computed } from '@angular/core';
import { BehaviorSubject, Observable, throwError, of } from 'rxjs';
import { tap, catchError, map, filter, take, switchMap } from 'rxjs/operators';
import _ from 'lodash';
import {
  DispatchInstruction,
  DispatchInstructionInsert,
  DispatchInstructionLine,
  DispatchInstructionStatus,
  ScannedRoll,
} from '../models/dispatch-instruction/dispatch-instruction';
import { DispatchEntryCommand } from '../models/dispatch-instruction/dispatch-entry-command';
import {
  AvailableRollsQuery,
  AvailableRollsResponse,
  BGRADE_ROLLS_PAGE_SIZE,
  BGradeRollsPageResponse,
} from '../models/dispatch-instruction/available-rolls';
import { DispatchInstructionProxyService } from '../proxies/inventory/dispatch-instruction.proxy.service';
import { LabDipRequestProxyService, Color } from '../proxies/item/lab-dip-request.proxy.service';
import { Note } from '@card-software/library';
import { WorkOrder } from '../models/order/work-order';
import { ProductionCard } from '../models/production/production-card';
import { Roll } from '../models/production/roll';

export type DispatchInstructionMode = 'add' | 'scan' | 'readonly';

@Injectable({
  providedIn: 'root',
})
export class DispatchInstructionService {
  //#region Private Signals
  private readonly _dispatchInstruction = signal<DispatchInstruction | null>(null);
  private readonly _availableRolls = signal<AvailableRollsResponse | null>(null);
  private readonly _mode = signal<DispatchInstructionMode>('add');
  private readonly _isProcessing = signal<boolean>(false);
  private readonly _selectedLineId = signal<string | null>(null);
  private readonly _editingEntry = signal<DispatchInstructionLine | null>(null); // Entry being edited (null = adding new)

  // Cache for colors
  private readonly _colorCache = signal<Map<string, Color>>(new Map());

  //#endregion

  //#region Public Readonly Signals
  readonly dispatchInstruction = this._dispatchInstruction.asReadonly();
  readonly availableRolls = this._availableRolls.asReadonly();
  readonly mode = this._mode.asReadonly();
  readonly isProcessing = this._isProcessing.asReadonly();
  readonly selectedLineId = this._selectedLineId.asReadonly();
  readonly editingEntry = this._editingEntry.asReadonly();
  readonly colorCache = this._colorCache.asReadonly();
  //#endregion

  //#region Computed Properties

  /**
   * All lines/entries in the dispatch instruction
   */
  readonly lines = computed(() => this._dispatchInstruction()?.lines ?? []);

  /**
   * The currently selected line/entry
   */
  readonly selectedLine = computed(() => {
    const lineId = this._selectedLineId();
    if (!lineId) return null;
    return this.lines().find((l) => l.lineId === lineId) ?? null;
  });

  /**
   * All scanned rolls across all lines
   */
  readonly allScannedRolls = computed(() => {
    const lines = this.lines();
    const rolls: ScannedRoll[] = [];
    for (const line of lines) {
      for (const fabric of line.fabricsAndBalances) {
        rolls.push(...fabric.scannedRolls);
      }
    }
    return rolls;
  });

  /**
   * Count of total rolls in the dispatch instruction
   */
  readonly totalRollCount = computed(() => this.allScannedRolls().length);

  /**
   * Count of scanned rolls (with scannedAt timestamp)
   */
  readonly scannedRollCount = computed(() => this.allScannedRolls().filter((r) => r.scannedAt).length);

  /**
   * Scan progress percentage
   */
  readonly scanProgress = computed(() => {
    const total = this.totalRollCount();
    if (total === 0) return 0;
    return Math.round((this.scannedRollCount() / total) * 100);
  });

  /**
   * Production card groups from available rolls
   */
  readonly productionCardGroups = computed(() => {
    const response = this._availableRolls();
    if (!response) return [];
    return Object.values(response.productionCardGroups);
  });

  /**
   * Check if the dispatch instruction is in draft status
   */
  readonly isDraft = computed(
    () => this._dispatchInstruction()?.status === DispatchInstructionStatus.Draft || this._dispatchInstruction() === null,
  );

  /**
   * Check if the dispatch instruction is in progress
   */
  readonly isInProgress = computed(() => this._dispatchInstruction()?.status === DispatchInstructionStatus.InProgress);

  /**
   * Check if the dispatch instruction is completed
   */
  readonly isCompleted = computed(() => this._dispatchInstruction()?.status === DispatchInstructionStatus.Completed);

  /**
   * Check if editing is allowed (only in draft status with add mode)
   */
  readonly canEdit = computed(() => this.isDraft() && this._mode() === 'add');

  /**
   * Check if scanning is allowed (only in progress status with scan mode)
   */
  readonly canScan = computed(() => this.isInProgress() && this._mode() === 'scan');

  /**
   * Check if currently editing an existing entry (vs adding new)
   */
  readonly isEditingEntry = computed(() => this._editingEntry() !== null);

  /**
   * Get fabric codes associated with the selected entry's color from the work order
   * This is used to filter available rolls by fabric code
   */
  readonly selectedEntryFabricCodes = computed<string[]>(() => {
    const entry = this._editingEntry() ?? this.selectedLine();
    if (!entry?.workOrder) return [];

    const colorRef = entry.colorReference;
    const fabricCodes: string[] = [];

    for (const fabric of entry.workOrder.fabrics ?? []) {
      for (const garmetDetail of fabric.garmetDetails ?? []) {
        if (garmetDetail.colorReference === colorRef) {
          if (!fabricCodes.includes(fabric.fabricCode)) {
            fabricCodes.push(fabric.fabricCode);
          }
        }
      }
    }
    return fabricCodes;
  });

  /**
   * Get the color reference for the selected/editing entry
   */
  readonly selectedEntryColorReference = computed<string | null>(() => {
    const entry = this._editingEntry() ?? this.selectedLine();
    return entry?.colorReference ?? null;
  });

  /**
   * Get the work order reference for the selected/editing entry
   * Used to filter available rolls by work order (matches ProductionCardRollGroup.workOrderReference)
   */
  readonly selectedEntryWorkOrderReference = computed<string | null>(() => {
    const entry = this._editingEntry() ?? this.selectedLine();
    return entry?.workOrderReference ?? null;
  });

  /**
   * Get work orders from the dispatch instruction lines
   */
  readonly workOrders = computed<WorkOrder[]>(() => {
    const lines = this.lines();
    const workOrders: WorkOrder[] = [];
    const seenIds = new Set<string>();

    for (const line of lines) {
      if (line.workOrder && !seenIds.has(line.workOrder.id)) {
        workOrders.push(line.workOrder);
        seenIds.add(line.workOrder.id);
      }
    }
    return workOrders;
  });

  /**
   * Get all production cards from the dispatch instruction lines
   */
  readonly productionCards = computed<ProductionCard[]>(() => {
    const lines = this.lines();
    const productionCards: ProductionCard[] = [];
    const seenIds = new Set<string>();

    for (const line of lines) {
      for (const pc of line.productionCards ?? []) {
        if (!seenIds.has(pc.id)) {
          productionCards.push(pc);
          seenIds.add(pc.id);
        }
      }
    }
    return productionCards;
  });

  //#endregion

  //#region Initialization State
  private _isInitialized = false;
  private readonly _isInitialized$ = new BehaviorSubject<boolean>(false);

  readonly isInitialized$ = this._isInitialized$.asObservable().pipe(
    filter((value) => value === true),
    take(1),
  );
  //#endregion

  constructor(
    private readonly dispatchInstructionProxyService: DispatchInstructionProxyService,
    private readonly labDipRequestProxyService: LabDipRequestProxyService,
  ) {}

  //#region Initialization Methods

  /**
   * Initialize the service with a dispatch instruction ID
   * @param id The dispatch instruction ID to load
   * @returns Observable that completes when initialized or errors if not found
   */
  initialize(id: string): Observable<DispatchInstruction> {
    const current = this._dispatchInstruction();
    if (this._isInitialized && !_.isNil(current) && current.id === id) {
      return of(current);
    }

    return this.dispatchInstructionProxyService.getById(id).pipe(
      map((response) => {
        if (!response.data) {
          throw new Error(`Dispatch Instruction not found with ID: ${id}`);
        }
        return response.data;
      }),
      tap((dispatchInstruction) => {
        this._dispatchInstruction.set(dispatchInstruction);
        this._isInitialized = true;
        this._isInitialized$.next(true);
        this.setModeBasedOnStatus(dispatchInstruction.status);
      }),
      catchError((error) => {
        console.error('Failed to load Dispatch Instruction:', error);
        this._isInitialized = false;
        this._isInitialized$.next(false);
        this._dispatchInstruction.set(null);
        return throwError(() => error);
      }),
    );
  }

  /**
   * Create a new dispatch instruction and initialize the service with it
   */
  createNew(command: DispatchInstructionInsert): Observable<DispatchInstruction> {
    this._isProcessing.set(true);
    return this.dispatchInstructionProxyService.create(command).pipe(
      map((response) => {
        if (!response.data) {
          throw new Error('Failed to create Dispatch Instruction');
        }
        return response.data;
      }),
      tap((dispatchInstruction) => {
        this._dispatchInstruction.set(dispatchInstruction);
        this._isInitialized = true;
        this._isInitialized$.next(true);
        this._mode.set('add');
        this._isProcessing.set(false);
      }),
      catchError((error) => {
        this._isProcessing.set(false);
        return throwError(() => error);
      }),
    );
  }

  getDispatchInstructionOrThrow(): DispatchInstruction {
    const di = this._dispatchInstruction();
    if (!di) {
      throw new Error('DispatchInstructionService must be initialized before use. Call initialize() first.');
    }
    return di;
  }

  isInitialized(): boolean {
    return this._isInitialized && this._dispatchInstruction() !== null;
  }

  reset(): void {
    this._dispatchInstruction.set(null);
    this._availableRolls.set(null);
    this._selectedLineId.set(null);
    this._editingEntry.set(null);
    this._mode.set('readonly');
    this._isInitialized = false;
    this._isInitialized$.next(false);
    this._colorCache.set(new Map());
  }

  /**
   * Refresh the dispatch instruction with new data (e.g., after priority update)
   */
  refreshDispatchInstruction(updated: DispatchInstruction): void {
    const preserved = this.preserveWorkOrderData(updated);
    this._dispatchInstruction.set(preserved);
  }

  //#endregion

  //#region Mode Management

  setMode(mode: DispatchInstructionMode): void {
    this._mode.set(mode);
  }

  private setModeBasedOnStatus(status: DispatchInstructionStatus): void {
    switch (status) {
      case DispatchInstructionStatus.Draft:
        this._mode.set('add');
        break;
      case DispatchInstructionStatus.InProgress:
        this._mode.set('scan');
        break;
      default:
        this._mode.set('readonly');
    }
  }

  //#endregion

  //#region Entry Management

  selectLine(lineId: string | null): void {
    this._selectedLineId.set(lineId);
  }

  /**
   * Start editing an existing entry
   * Sets the editing entry and selects the line
   */
  startEditingEntry(line: DispatchInstructionLine): void {
    this._editingEntry.set(line);
    this._selectedLineId.set(line.lineId);
  }

  /**
   * Stop editing and clear the editing entry
   * Optionally clears the selected line as well
   */
  stopEditingEntry(clearSelection = false): void {
    this._editingEntry.set(null);
    if (clearSelection) {
      this._selectedLineId.set(null);
    }
  }

  /**
   * Clear entry form state (for adding new entry)
   */
  clearEntryForm(): void {
    this._editingEntry.set(null);
    this._selectedLineId.set(null);
  }

  /**
   * Add or update an entry (line) in the dispatch instruction
   */
  upsertEntry(command: DispatchEntryCommand): Observable<DispatchInstruction> {
    const di = this.getDispatchInstructionOrThrow();
    this._isProcessing.set(true);

    return this.dispatchInstructionProxyService.upsertEntry(di.id, command).pipe(
      map((response) => {
        if (!response.data) {
          throw new Error('Failed to upsert entry');
        }
        return response.data;
      }),
      tap((updated) => {
        this._dispatchInstruction.set(updated);
        this._isProcessing.set(false);
      }),
      catchError((error) => {
        this._isProcessing.set(false);
        return throwError(() => error);
      }),
    );
  }

  /**
   * Remove an entry from the dispatch instruction
   */
  removeEntry(lineId: string): Observable<DispatchInstruction> {
    const di = this.getDispatchInstructionOrThrow();
    this._isProcessing.set(true);

    return this.dispatchInstructionProxyService.removeEntry(di.id, lineId).pipe(
      map((response) => {
        if (!response.data) {
          throw new Error('Failed to remove entry');
        }
        return response.data;
      }),
      tap((updated) => {
        this._dispatchInstruction.set(updated);
        if (this._selectedLineId() === lineId) {
          this._selectedLineId.set(null);
        }
        this._isProcessing.set(false);
      }),
      catchError((error) => {
        this._isProcessing.set(false);
        return throwError(() => error);
      }),
    );
  }

  //#endregion

  //#region Roll Management

  /**
   * Load available rolls for the given work order references
   */
  loadAvailableRolls(workOrderReferences: string[]): Observable<AvailableRollsResponse> {
    const query: AvailableRollsQuery = { workOrderReferences };
    this._isProcessing.set(true);

    return this.dispatchInstructionProxyService.getAvailableRolls(query).pipe(
      map((response) => {
        if (!response.data) {
          return { productionCardGroups: {} };
        }
        return response.data;
      }),
      tap((result) => {
        this._availableRolls.set(result);
        this._isProcessing.set(false);
      }),
      catchError((error) => {
        this._isProcessing.set(false);
        return throwError(() => error);
      }),
    );
  }

  /**
   * Load a page of available B-Grade rolls.
   * Pagination and search run on the API (the B-Grade set is too large to filter in the browser),
   * so `search` matches roll numbers / Production Card numbers over the whole dataset, not just the
   * loaded page. The Standard and GreigeOnly flows keep loading their full pre-filtered response.
   */
  loadBGradeAvailableRolls(pageNumber = 1, pageSize = BGRADE_ROLLS_PAGE_SIZE, search?: string): Observable<BGradeRollsPageResponse> {
    this._isProcessing.set(true);

    return this.dispatchInstructionProxyService.getBGradeAvailableRolls(pageNumber, pageSize, search).pipe(
      map((response) => {
        if (!response.data) {
          return { productionCardGroups: [], totalCount: 0, pageSize, pageNumber };
        }
        return response.data;
      }),
      tap((result) => {
        this._availableRolls.set({ productionCardGroups: _.keyBy(result.productionCardGroups, (group) => group.productionCardNumber) });
        this._isProcessing.set(false);
      }),
      catchError((error) => {
        this._isProcessing.set(false);
        return throwError(() => error);
      }),
    );
  }

  /**
   * Load available GreigeOnly rolls in finished goods for the given work order references
   */
  loadGreigeOnlyAvailableRolls(workOrderReferences: string[]): Observable<AvailableRollsResponse> {
    const query: AvailableRollsQuery = { workOrderReferences };
    this._isProcessing.set(true);

    return this.dispatchInstructionProxyService.getGreigeOnlyAvailableRolls(query).pipe(
      map((response) => {
        if (!response.data) {
          return { productionCardGroups: {} };
        }
        return response.data;
      }),
      tap((result) => {
        this._availableRolls.set(result);
        this._isProcessing.set(false);
      }),
      catchError((error) => {
        this._isProcessing.set(false);
        return throwError(() => error);
      }),
    );
  }

  /**
   * Preserve work order data from current dispatch instruction when updating
   * The API response may not include populated workOrder objects
   */
  private preserveWorkOrderData(updated: DispatchInstruction): DispatchInstruction {
    const current = this._dispatchInstruction();
    if (!current) return updated;

    // Create a map of lineId -> workOrder from current state
    const workOrderMap = new Map<string, WorkOrder>();
    for (const line of current.lines) {
      if (line.workOrder) {
        workOrderMap.set(line.lineId, line.workOrder);
      }
    }

    // Merge work order data into updated lines
    const updatedLines = updated.lines.map((line) => {
      if (!line.workOrder && workOrderMap.has(line.lineId)) {
        return { ...line, workOrder: workOrderMap.get(line.lineId) };
      }
      return line;
    });

    return { ...updated, lines: updatedLines };
  }

  /**
   * Add rolls to a specific entry
   */
  addRollsToEntry(lineId: string, rollNumbers: string[]): Observable<DispatchInstruction> {
    const di = this.getDispatchInstructionOrThrow();
    this._isProcessing.set(true);

    return this.dispatchInstructionProxyService.addRollsToEntry(di.id, lineId, rollNumbers).pipe(
      map((response) => {
        if (!response.data) {
          throw new Error('Failed to add rolls to entry');
        }
        return response.data;
      }),
      tap((updated) => {
        this._dispatchInstruction.set(this.preserveWorkOrderData(updated));
        this._isProcessing.set(false);
      }),
      catchError((error) => {
        this._isProcessing.set(false);
        return throwError(() => error);
      }),
    );
  }

  /**
   * Remove rolls from a specific entry
   */
  removeRollsFromEntry(lineId: string, rollNumbers: string[]): Observable<DispatchInstruction> {
    const di = this.getDispatchInstructionOrThrow();
    this._isProcessing.set(true);

    return this.dispatchInstructionProxyService.removeRollsFromEntry(di.id, lineId, rollNumbers).pipe(
      map((response) => {
        if (!response.data) {
          throw new Error('Failed to remove rolls from entry');
        }
        return response.data;
      }),
      tap((updated) => {
        this._dispatchInstruction.set(this.preserveWorkOrderData(updated));
        this._isProcessing.set(false);
      }),
      catchError((error) => {
        this._isProcessing.set(false);
        return throwError(() => error);
      }),
    );
  }

  /**
   * Scan a roll (mark it as scanned with timestamp and user)
   */
  scanRoll(rollNumber: string): Observable<DispatchInstruction> {
    const di = this.getDispatchInstructionOrThrow();
    this._isProcessing.set(true);

    return this.dispatchInstructionProxyService.scanRoll(di.id, rollNumber).pipe(
      map((response) => {
        if (!response.data) {
          throw new Error('Failed to scan roll');
        }
        return response.data;
      }),
      tap((updated) => {
        this._dispatchInstruction.set(this.preserveWorkOrderData(updated));
        this._isProcessing.set(false);
      }),
      catchError((error) => {
        this._isProcessing.set(false);
        return throwError(() => error);
      }),
    );
  }

  //#endregion

  //#region Status Management

  /**
   * Update the status of the dispatch instruction
   */
  updateStatus(newStatus: DispatchInstructionStatus): Observable<DispatchInstruction> {
    const di = this.getDispatchInstructionOrThrow();
    this._isProcessing.set(true);

    return this.dispatchInstructionProxyService.updateStatus(di.id, newStatus).pipe(
      map((response) => {
        if (!response.data) {
          throw new Error('Failed to update status');
        }
        return response.data;
      }),
      tap((updated) => {
        const preserved = this.preserveWorkOrderData(updated);
        this._dispatchInstruction.set(preserved);
        this.setModeBasedOnStatus(preserved.status);
        this._isProcessing.set(false);
      }),
      catchError((error) => {
        this._isProcessing.set(false);
        return throwError(() => error);
      }),
    );
  }

  /**
   * Send dispatch instruction to warehouse (Draft -> SentToWarehouse)
   */
  sendToWarehouse(): Observable<DispatchInstruction> {
    return this.updateStatus(DispatchInstructionStatus.SentToWarehouse);
  }

  /**
   * Start processing (SentToWarehouse -> InProgress)
   */
  startProcessing(): Observable<DispatchInstruction> {
    return this.updateStatus(DispatchInstructionStatus.InProgress);
  }

  /**
   * Complete the dispatch instruction (InProgress -> Completed)
   */
  complete(): Observable<DispatchInstruction> {
    return this.updateStatus(DispatchInstructionStatus.Completed);
  }

  /**
   * Cancel the dispatch instruction
   */
  cancel(): Observable<DispatchInstruction> {
    return this.updateStatus(DispatchInstructionStatus.Cancelled);
  }

  //#endregion

  //#region Notes Management

  /**
   * Update notes for the dispatch instruction
   */
  updateNotes(notes: Note[]): Observable<DispatchInstruction> {
    const di = this.getDispatchInstructionOrThrow();
    this._isProcessing.set(true);

    return this.dispatchInstructionProxyService.updateNotes(di.id, notes).pipe(
      map((response) => {
        if (!response.data) {
          throw new Error('Failed to update notes');
        }
        return response.data;
      }),
      tap((updated) => {
        this._dispatchInstruction.set(updated);
        this._isProcessing.set(false);
      }),
      catchError((error) => {
        this._isProcessing.set(false);
        return throwError(() => error);
      }),
    );
  }

  //#endregion

  //#region Utility Methods

  /**
   * Get rolls for a specific line grouped by fabric code
   */
  getRollsForLine(lineId: string): Map<string, ScannedRoll[]> {
    const line = this.lines().find((l) => l.lineId === lineId);
    if (!line) return new Map();

    const result = new Map<string, ScannedRoll[]>();
    for (const fabric of line.fabricsAndBalances) {
      result.set(fabric.fabricCode, fabric.scannedRolls);
    }
    return result;
  }

  /**
   * Get total weight for a line (in lbs)
   */
  getLineTotalWeight(lineId: string, availableRolls: Roll[]): number {
    const line = this.lines().find((l) => l.lineId === lineId);
    if (!line) return 0;

    const rollNumbers = new Set<string>();
    for (const fabric of line.fabricsAndBalances) {
      for (const roll of fabric.scannedRolls) {
        rollNumbers.add(roll.rollNumber);
      }
    }

    return availableRolls
      .filter((r) => rollNumbers.has(r.number ?? ''))
      .reduce((sum, r) => sum + (r.finalWeight?.weightInLbs ?? r.netQuantity ?? 0), 0);
  }

  /**
   * Convert weight from lbs to kg
   */
  lbsToKg(lbs: number): number {
    return lbs * 0.453592;
  }

  //#endregion

  //#region Color Cache Methods

  /**
   * Get color name from cache
   */
  getColorName(colorId: string): string | null {
    const cached = this._colorCache().get(colorId);
    return cached?.name ?? null;
  }

  /**
   * Get the approved recipe (lab dip) number for a color from cache
   */
  getColorApprovedRecipeNumber(colorId: string): string | null {
    return this._colorCache().get(colorId)?.approvedRecipeNumber ?? null;
  }

  /**
   * Load color names for the given color IDs (only fetches uncached)
   */
  loadColorNames(colorIds: (string | undefined)[]): Observable<void> {
    const currentCache = this._colorCache();
    const uncachedIds = _.uniq(colorIds.filter((id): id is string => !!id && !currentCache.has(id)));

    if (uncachedIds.length === 0) {
      return of(undefined);
    }

    return this.labDipRequestProxyService.getColorsByIds(uncachedIds).pipe(
      tap((response) => {
        if (response.data) {
          const newCache = new Map(this._colorCache());
          for (const color of response.data) {
            newCache.set(color.id, color);
          }
          this._colorCache.set(newCache);
        }
      }),
      map(() => undefined),
      catchError((error) => {
        console.error('Failed to load color names:', error);
        return of(undefined);
      }),
    );
  }

  /**
   * Load every distinct color of the given work orders into the cache in one batch call
   */
  loadWorkOrderColors(workOrders: WorkOrder[]): Observable<void> {
    const colorIds = workOrders.flatMap((workOrder) =>
      (workOrder.fabrics ?? []).flatMap((fabric) => (fabric.garmetDetails ?? []).map((detail) => detail.colorReference)),
    );
    return this.loadColorNames(colorIds);
  }

  /**
   * Load color names for the current dispatch instruction: the colors of its work orders plus the
   * entries' own colors, in case an entry's work order is not populated
   */
  loadCachedData(): Observable<void> {
    const lineColorIds = this.lines().map((line) => line.colorReference);
    return this.loadWorkOrderColors(this.workOrders()).pipe(switchMap(() => this.loadColorNames(lineColorIds)));
  }

  //#endregion
}
